<template>
  <div id="deadDealMessageBox" class="box">
    <span class="text">
      <icon-base class="mr-2 h-4 w-4" :icon="IconFailBigRed" />
      <span class="highlight whitespace-nowrap" data-cy="deadReason">
        {{ reason }}
      </span>
      <span v-if="subReason" class="highlight" data-cy="deadSubReason">
        : {{ subReason }}
      </span>
      &nbsp;
      <span v-if="notes" class="ml-1" data-cy="deadNotes">
        {{ notes }}
      </span>
      <span class="date" data-cy="dealDeadDateTime">
        {{ dateTime }}
      </span>
    </span>
  </div>
</template>
<script setup lang="ts">
import { computed } from "vue";
import { useStore } from "vuex";

import { formatDateCustom } from "@/helpers/formatting";

import IconFailBigRed from "@/components/icons/IconFailBigRed.vue";
import IconBase from "@/components/ui/IconBase.vue";

import type { IApplicationDeath } from "@/models/applications";
import type { PropType } from "vue";

const props = defineProps({
  death: {
    type: Object as PropType<IApplicationDeath>,
    default: null,
  },
});

const { getters } = useStore();
const deadReasons = computed<Record<string, string>>(() =>
  getters["options/deadReasons"]()
);

const deadSubReasons = computed<Record<string, string>>(() =>
  getters["options/deadSubReasons"]()
);

const reason = computed(() =>
  getValueFromOptions(props.death?.reason, deadReasons.value)
);
const subReason = computed(() =>
  getValueFromOptions(props.death?.sub_reason, deadSubReasons.value)
);

const notes = computed<string | null>(() => props?.death?.notes || null);

const dateTime = computed(() => {
  return formatDateCustom(props?.death?.created_at, "MM/dd/yyyy hh:mm a", true);
});

const getValueFromOptions = (
  value: number | null | string,
  options: Record<string, string>
) => {
  const parsedValue = Number(value);
  const isValid = !Number.isNaN(parsedValue);
  return isValid ? options[parsedValue] : value;
};
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.box {
  @apply flex bg-red-50 px-6 py-5;
}
.box .text {
  @apply line-clamp-1 w-full break-words;
}
.box:hover .text {
  @apply line-clamp-none overflow-visible;
}
.highlight {
  @apply font-semibold text-red-500;
}
.date {
  @apply ml-2 italic;
}
</style>
